import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  buildSpanTree,
  buildMessageRetryMap,
  deriveAgentStatusFromExecMessage,
  flattenSpanTree,
  isTerminalWfStatus,
  normalizeEventKey,
  normalizeStatus,
  preprocessExecMessages,
  resolveAgentIdFromMessage,
  messageMatchesTraceExecution,
  resolveWfStatusFromMessage,
  summarizeTraceMetrics,
  unwrapWorkflowMessage
} from './executionHistoryUtils';

import diagramPng from '../../assets/img/diagram.png';
import { formatDuration, formatDateTime } from '../../settings/shared/commonMethods';
import slackIcon from '../../assets/img/icons/svg/slack.svg';
import gmailIcon from '../../assets/img/icons/svg/gmail.svg';

const COLORS = {
  bg: '#ffffff',
  muted: '#6b7280',
  text: '#111827',
  border: '#e5e7eb',
  soft: '#f9fafb',
  blue: '#3b82f6',
  green: '#1fc694',
  red: '#ef4444',
  amber: '#f59e0b'
};

const MarkdownBlock = ({ text }) => {
  const coerceToMarkdown = (input) => {
    if (input == null) return '';
    if (typeof input === 'string') return input;
    if (typeof input === 'number' || typeof input === 'boolean') return String(input);
    try {
      return `\`\`\`json\n${JSON.stringify(input, null, 2)}\n\`\`\``;
    } catch (_) {
      return String(input);
    }
  };

  const value = coerceToMarkdown(text);
  if (String(value).trim() === '') {
    return <div style={{ color: COLORS.muted, fontSize: 13 }}>—</div>;
  }

  return (
    <div style={{ color: COLORS.text, fontSize: 13, lineHeight: 1.6, overflowWrap: 'anywhere' }}>
      <ReactMarkdown
        components={{
          p: ({ children }) => <p style={{ margin: '0 0 10px' }}>{children}</p>,
          ul: ({ children }) => <ul style={{ margin: '0 0 10px', paddingLeft: 18 }}>{children}</ul>,
          ol: ({ children }) => <ol style={{ margin: '0 0 10px', paddingLeft: 18 }}>{children}</ol>,
          li: ({ children }) => <li style={{ margin: '4px 0' }}>{children}</li>,
          code: ({ inline, children }) =>
            inline ? (
              <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 6, fontSize: 12 }}>
                {children}
              </code>
            ) : (
              <pre
                style={{
                  background: '#0b1220',
                  color: '#e2e8f0',
                  padding: 12,
                  borderRadius: 10,
                  overflow: 'auto',
                  margin: '0 0 10px'
                }}
              >
                <code style={{ fontSize: 12 }}>{children}</code>
              </pre>
            ),
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noreferrer" style={{ color: COLORS.blue, textDecoration: 'underline' }}>
              {children}
            </a>
          )
        }}
      >
        {value}
      </ReactMarkdown>
    </div>
  );
};

const pill = ({ text, bg, color, icon }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 10px',
      borderRadius: '999px',
      backgroundColor: bg,
      color,
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: 1
    }}
  >
    {icon ? <i className={icon} style={{ fontSize: '12px' }} /> : null}
    {text}
  </span>
);

const StatusBadge = ({ status }) => {
  if (String(status || '').toLowerCase() === 'loading') {
    return (
      <span
        className="status-badge status-pending"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '12px'
        }}
      >
        <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '12px' }} />
        <span>Loading</span>
      </span>
    );
  }
  const normalized = normalizeStatus(status);
  let className = 'status-badge';
  let label = 'Status';
  if (normalized === 'success') {
    className = 'status-badge status-completed';
    label = 'Success';
  } else if (normalized === 'error') {
    className = 'status-badge status-failed';
    label = 'Failed';
  } else if (normalized === 'skipped') {
    className = 'status-badge status-pending';
    label = 'Not executed';
  } else if (normalized === 'running' || normalized === 'pending') {
    className = 'status-badge status-pending';
    label = 'Running';
  } else if (normalized && normalized !== 'unknown') {
    label = normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px'
      }}
    >
      <span
        style={{
          width: '6px',
          height: '6px',
          borderRadius: '999px',
          backgroundColor: 'currentColor'
        }}
      />
      <span>{label}</span>
    </span>
  );
};

const kindStyle = (kind) => {
  switch (String(kind || '').toLowerCase()) {
    case 'project':
      return { bg: '#eef2ff', color: '#4f46e5', icon: 'fa-solid fa-list' };
    case 'agent':
      return { bg: '#e8f5ff', color: '#0b74d1', icon: 'fa-solid fa-diagram-project' };
    case 'llm':
      return { bg: '#efe8ff', color: '#7c3aed', icon: 'fa-solid fa-brain' };
    case 'retriever':
      return { bg: '#ffe9e9', color: '#ef4444', icon: 'fa-solid fa-magnifying-glass' };
    case 'tool':
      return { bg: '#fff6d9', color: '#b45309', icon: 'fa-solid fa-wrench' };
    case 'chain':
      return { bg: '#e9f7ff', color: '#0369a1', icon: 'fa-solid fa-link' };
    default:
      return { bg: '#f3f4f6', color: '#374151', icon: 'fa-regular fa-circle' };
  }
};

const isTaskAgentNode = (node, traceAgentConfigList) => {
  if (!node || String(node.kind || '').toLowerCase() !== 'agent') return false;
  const agentId =
    node?.attributes?.agentId ||
    node?.attributes?.agent_id ||
    node?.attributes?.agentId?.agentId ||
    null;
  const agentName =
    node?.attributes?.agentName ||
    node?.name ||
    null;
  const list = Array.isArray(traceAgentConfigList) ? traceAgentConfigList : [];
  const cfg =
    list.find((c) => agentId && String(c?.agentId) === String(agentId)) ||
    list.find((c) => agentName && (c?.title === agentName || c?.name === agentName)) ||
    null;

  // In our workflow payload, Task Agents carry `engines` (LLM agents carry `llm`).
  if (Array.isArray(cfg?.engines) && cfg.engines.length > 0) return true;

  // Task agent node ids are prefixed with "task-" in workflow payloads.
  if (agentId && String(agentId).toLowerCase().startsWith('task-')) return true;

  // Last-resort heuristic to keep UI sensible if config isn't present.
  return typeof agentName === 'string' && agentName.toLowerCase().includes('task');
};

const isNodeUnderTaskAgent = (node, flatItems, traceAgentConfigList) => {
  if (!node || !Array.isArray(flatItems) || flatItems.length === 0) return false;
  const byId = new Map(flatItems.map((x) => [x.node.id, x]));
  let cur = byId.get(node.id);
  while (cur?.node) {
    if (isTaskAgentNode(cur.node, traceAgentConfigList)) return true;
    cur = cur.parent ? byId.get(cur.parent.id) : null;
  }
  return false;
};

const MetricCard = ({
  icon,
  label,
  value,
  iconBackgroundColor = '#8b5cf6',
  cardBackgroundColor = '#faf5ff',
  iconColor = '#ffffff'
}) => (
  <div
    style={{
      border: `1px solid ${COLORS.border}`,
      borderRadius: '14px',
      padding: '16px 18px',
      backgroundColor: cardBackgroundColor,
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
    }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          backgroundColor: iconBackgroundColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
        aria-hidden
      >
        <i className={icon} style={{ color: iconColor, fontSize: '18px' }} />
    </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            color: '#64748b',
            fontSize: '13px',
            fontWeight: 600,
            letterSpacing: '0.01em'
          }}
        >
          {label}
        </div>
        <div
          style={{
            marginTop: '6px',
            fontSize: '12px',
            fontWeight: 700,
            color: '#0f172a',
            letterSpacing: '-0.03em',
            lineHeight: 1.25,
            wordBreak: 'break-word'
          }}
        >
          {value}
        </div>
      </div>
    </div>
  </div>
);

const Table = ({ rows, headerKey = 'KEY', headerValue = 'VALUE', accentColor = null }) => {
  const headerBg = accentColor
    ? `linear-gradient(90deg, ${accentColor}14 0%, ${accentColor}08 55%, #f8fafc 100%)`
    : 'linear-gradient(90deg, #3b82f614 0%, #8b5cf614 50%, #f8fafc 100%)';
  const headerColor = accentColor ? accentColor : COLORS.muted;
  const keyColor = '#7c3aed'; // purple
  const valueColor = COLORS.text; // black
  const isErrorKey = (k) => typeof k === 'string' && k.toLowerCase().includes('error');
  const wrapCell = {
    minWidth: 0,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    lineHeight: 1.4
  };

  const renderCell = (cell) => {
    if (React.isValidElement(cell)) return cell;
    if (cell == null) return '—';
    if (typeof cell === 'string' || typeof cell === 'number' || typeof cell === 'boolean') return String(cell);
    try {
      return JSON.stringify(cell, null, 2);
    } catch (_) {
      return String(cell);
    }
  };

  return (
    <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: '12px', overflow: 'hidden', backgroundColor: COLORS.bg }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', borderBottom: `1px solid ${COLORS.border}`, background: headerBg }}>
        <div style={{ padding: '12px 14px', width: '250px', fontSize: '12px', fontWeight: 700, color: headerColor, letterSpacing: '0.06em' }}>{headerKey}</div>
        <div style={{ padding: '12px 14px', fontSize: '12px', fontWeight: 700, color: headerColor, letterSpacing: '0.06em' }}>{headerValue}</div>
      </div>
    {rows.length === 0 ? (
      <div style={{ padding: '14px', color: COLORS.muted, fontSize: '13px' }}>No data</div>
    ) : (
      rows.map(([k, v], idx) => (
        <div
          key={`${headerKey}-${idx}`}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            borderBottom: `1px solid ${COLORS.border}`,
            backgroundColor: isErrorKey(k) ? '#fff1f2' : 'transparent'
          }}
        >
          <div
            title={typeof k === 'string' ? k : undefined}
            style={{
              padding: '12px 14px',
              fontSize: '13px',
              width: '250px',
              color: keyColor,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              ...wrapCell
            }}
          >
            {renderCell(k)}
          </div>
          <div
            title={typeof v === 'string' ? v : undefined}
            style={{
              padding: '12px 14px',
              fontSize: '13px',
              color: valueColor,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              ...wrapCell
            }}
          >
            {renderCell(v)}
          </div>
        </div>
      ))
    )}
    </div>
  );
};

const APPROVAL_CHANNEL_META = {
  slack: { label: 'Slack', icon: slackIcon },
  gmail: { label: 'Gmail', icon: gmailIcon },
  email: { label: 'Email', icon: gmailIcon },
};

const hasApprovalPayload = (value) => {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.values(value).some((v) => v != null && String(v).trim() !== '');
};

const pickApprovalFromMessage = (msg) => {
  if (!msg) return null;
  const candidates = [msg.approval, msg.data?.approval];
  for (const candidate of candidates) {
    if (hasApprovalPayload(candidate)) return candidate;
  }
  return null;
};

const normalizeApproval = (raw) => ({
  requestId: raw.requestId || raw.request_id || '',
  runId: raw.runId || raw.run_id || '',
  agentId: raw.agentId || raw.agent_id || '',
  agentName: raw.agentName || raw.agent_name || '',
  approvalStatus: String(raw.approvalStatus || raw.approval_status || raw.status || '').trim(),
  approvalChannel: String(raw.approvalChannel || raw.approval_channel || raw.channel || '').trim(),
  message: raw.message || raw.approvalMessage || raw.approval_message || '',
  recipient: raw.recipient || raw.approvalRecipient || raw.approval_recipient || '',
  respondedAt: raw.respondedAt || raw.responded_at || '',
});

const approvalStatusMeta = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'APPROVED' || s === 'ACCEPT' || s === 'ACCEPTED') {
    return { label: 'APPROVED', bg: '#ecfdf5', color: '#047857', icon: 'fa-solid fa-check', bar: '#10b981' };
  }
  if (s === 'REJECTED' || s === 'DENIED' || s === 'DECLINED') {
    return { label: s, bg: '#fef2f2', color: '#b91c1c', icon: 'fa-solid fa-xmark', bar: '#ef4444' };
  }
  if (s === 'PENDING' || s === 'REQUESTED' || s === 'WAITING') {
    return { label: s, bg: '#fffbeb', color: '#b45309', icon: 'fa-regular fa-clock', bar: '#f59e0b' };
  }
  return { label: s || 'Unknown', bg: '#f3f4f6', color: '#374151', icon: 'fa-regular fa-circle-check', bar: '#8b5cf6' };
};

const recipientInitial = (recipient) => {
  const value = String(recipient || '').trim();
  if (!value) return '?';
  const local = value.split('@')[0] || value;
  return local.charAt(0).toUpperCase();
};

const ApprovalCard = ({ approval }) => {
  const status = approvalStatusMeta(approval.approvalStatus);
  const channelKey = String(approval.approvalChannel || '').toLowerCase();
  const channel = APPROVAL_CHANNEL_META[channelKey] || null;
  const channelLabel = channel?.label || approval.approvalChannel || '—';
  const message = String(approval.message || '').trim();
  const recipient = String(approval.recipient || '').trim();

  const rows = [
    ['Status', (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 10px',
          borderRadius: 999,
          background: status.bg,
          color: status.color,
          fontWeight: 700,
          fontSize: 12,
        }}
      >
        <i className={status.icon} />
        {status.label}
      </span>
    )],
    ['Channel', (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {channel?.icon ? (
          <img src={channel.icon} alt="" width={16} height={16} style={{ objectFit: 'contain' }} />
        ) : null}
        {channelLabel}
      </span>
    )],
    ['Agent', approval.agentName || '—'],
    ['Agent ID', approval.agentId || '—'],
    ['Request ID', approval.requestId || '—'],
    ['Run ID', approval.runId || '—'],
    ['Responded', approval.respondedAt ? formatDateTime(approval.respondedAt) : '—'],
  ];

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div
        style={{
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: COLORS.bg,
        }}
      >
        <div
          style={{
            padding: '14px 16px',
            background: `linear-gradient(90deg, ${status.bar}14 0%, #8b5cf610 55%, #f8fafc 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <span
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: status.bg,
                color: status.color,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <i className="fa-solid fa-user-check" />
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: status.color, letterSpacing: '0.06em' }}>
                HUMAN APPROVAL
              </div>
              <div style={{ fontSize: 13, color: COLORS.text, marginTop: 4, fontWeight: 600 }}>
                {approval.agentName || 'Agent'}
                {approval.approvalChannel ? ` · ${channelLabel}` : ''}
              </div>
            </div>
          </div>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px',
              borderRadius: 999,
              background: status.bg,
              color: status.color,
              fontWeight: 700,
              fontSize: 12,
            }}
          >
            <i className={status.icon} />
            {status.label}
          </span>
        </div>
      </div>

      {(recipient || message) ? (
        <div
          style={{
            border: `1px solid ${COLORS.border}`,
            borderRadius: 14,
            overflow: 'hidden',
            background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 42%)',
          }}
        >
          {recipient ? (
            <div
              style={{
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
                borderBottom: message ? `1px solid ${COLORS.border}` : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <span
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: 14,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {recipientInitial(recipient)}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: '0.08em' }}>
                    SENT TO
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: COLORS.text,
                      marginTop: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={recipient}
                  >
                    {recipient}
                  </div>
                </div>
              </div>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '5px 10px',
                  borderRadius: 999,
                  background: '#fff',
                  border: `1px solid ${COLORS.border}`,
                  fontSize: 12,
                  fontWeight: 700,
                  color: COLORS.text,
                }}
              >
                {channel?.icon ? (
                  <img src={channel.icon} alt="" width={14} height={14} style={{ objectFit: 'contain' }} />
                ) : (
                  <i className="fa-regular fa-paper-plane" style={{ color: COLORS.muted, fontSize: 11 }} />
                )}
                {channelLabel}
              </span>
            </div>
          ) : null}

          {message ? (
            <div style={{ padding: '16px 16px 14px' }}>
              <style>
                {`
                  .sa-approval-msg-scroll {
                    max-height: 232px;
                    overflow-y: scroll;
                    overflow-x: hidden;
                    scrollbar-gutter: stable;
                    scrollbar-width: thin;
                    scrollbar-color: #818cf8 #e0e7ff;
                  }
                  .sa-approval-msg-scroll::-webkit-scrollbar {
                    width: 8px;
                  }
                  .sa-approval-msg-scroll::-webkit-scrollbar-track {
                    background: #e0e7ff;
                    border-radius: 8px;
                  }
                  .sa-approval-msg-scroll::-webkit-scrollbar-thumb {
                    background: #818cf8;
                    border-radius: 8px;
                  }
                `}
              </style>
              <div style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: '0.08em', marginBottom: 10 }}>
                REQUEST MESSAGE
              </div>
              <div
                className="sa-approval-msg-scroll"
                style={{
                  position: 'relative',
                  background: '#eef2ff',
                  border: '1px solid #e0e7ff',
                  borderRadius: '4px 16px 16px 16px',
                  padding: '14px 16px 14px 18px',
                  boxShadow: '0 8px 20px rgba(79, 70, 229, 0.06)',
                }}
              >
                <span
                  style={{
                    position: 'sticky',
                    top: 0,
                    left: 0,
                    color: '#818cf8',
                    fontSize: 22,
                    lineHeight: 1,
                    fontFamily: 'Georgia, serif',
                    opacity: 0.7,
                    display: 'block',
                    height: 0,
                  }}
                >
                  “
                </span>
                <div
                  style={{
                    paddingLeft: 14,
                    fontSize: 15,
                    lineHeight: 1.55,
                    fontWeight: 600,
                    color: '#1e1b4b',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {message}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <Table rows={rows} headerKey="KEY" headerValue="VALUE" accentColor={status.bar} />
    </div>
  );
};

const TokenAccordion = ({ items, expandedIds, onToggle }) => (
  <div>
    {/* Column headers */}
    <div style={{ marginBottom: '10px', border: `1px solid ${COLORS.border}`, borderRadius: '12px', overflow: 'hidden', backgroundColor: COLORS.bg }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1fr 1fr 1fr 1fr', borderBottom: `1px solid ${COLORS.border}`, background: 'linear-gradient(90deg, #3b82f614 0%, #8b5cf614 50%, #f8fafc 100%)' }}>
        {['Event', 'Prompt', 'Completion', 'Reasoning', 'Total'].map((h) => (
          <div key={h} style={{ padding: '12px 14px', fontSize: '12px', fontWeight: 800, color: COLORS.muted, letterSpacing: '0.06em' }}>
            {h.toUpperCase()}
          </div>
        ))}
      </div>
    </div>

    {items.length === 0 ? (
      <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: '12px', padding: '14px', color: COLORS.muted, fontSize: '13px', backgroundColor: COLORS.bg }}>
        No token data
      </div>
    ) : (
      items.map((it) => {
        const isOpen = expandedIds.has(it.id);
        const hasDetails = Boolean(it.completionTokensDetails || it.promptTokensDetails);
        return (
          <div key={it.id} style={{ border: `1px solid ${COLORS.border}`, borderRadius: '12px', overflow: 'hidden', backgroundColor: COLORS.bg, marginBottom: '10px' }}>
            <button
              onClick={() => onToggle(it.id)}
              style={{
                width: '100%',
                textAlign: 'left',
                border: 'none',
                background: '#f8fafc',
                padding: '12px 14px',
                cursor: hasDetails ? 'pointer' : 'default',
                display: 'grid',
                gridTemplateColumns: '1.8fr 1fr 1fr 1fr 1fr',
                alignItems: 'center',
                gap: '10px'
              }}
              disabled={!hasDetails}
              title={hasDetails ? 'Expand token details' : ''}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                <i className={`fa-solid ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`} style={{ fontSize: '11px', color: hasDetails ? COLORS.muted : '#d1d5db' }} />
                <span style={{ fontSize: '13px', color: COLORS.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {it.event}
                </span>
              </div>
              <span style={{ fontSize: '13px', color: COLORS.muted, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>{it.prompt ?? '—'}</span>
              <span style={{ fontSize: '13px', color: COLORS.muted, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>{it.completion ?? '—'}</span>
              <span style={{ fontSize: '13px', color: COLORS.muted, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>{it.reasoning ?? '—'}</span>
              <span style={{ fontSize: '13px', color: COLORS.muted, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>{it.total ?? '—'}</span>
            </button>

            {isOpen && hasDetails ? (
              <div style={{ padding: '12px 12px 14px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: COLORS.muted, letterSpacing: '0.06em', marginBottom: '8px' }}>
                      COMPLETION TOKENS DETAILS
                    </div>
                    <Table
                      rows={Object.entries(it.completionTokensDetails || {}).filter(([, v]) => v != null).map(([k, v]) => [k, toDisplayString(v)])}
                      accentColor={COLORS.blue}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: COLORS.muted, letterSpacing: '0.06em', marginBottom: '8px' }}>
                      PROMPT TOKENS DETAILS
                    </div>
                    <Table
                      rows={Object.entries(it.promptTokensDetails || {}).filter(([, v]) => v != null).map(([k, v]) => [k, toDisplayString(v)])}
                      accentColor={COLORS.amber}
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        );
      })
    )}
  </div>
);

const toDisplayString = (v) => {
  if (v == null) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v, null, 2);
  } catch (_) {
    return String(v);
  }
};

const pushIfPresent = (rows, key, value) => {
  if (value == null) return;
  if (typeof value === 'string' && value.trim() === '') return;
  rows.push([key, toDisplayString(value)]);
};

const isPlainObject = (v) =>
  v != null && typeof v === 'object' && !Array.isArray(v) && Object.prototype.toString.call(v) === '[object Object]';

const pushStructured = (rows, key, value, { maxDepth = 4, maxArrayItems = 25 } = {}) => {
  const walk = (prefix, v, depth) => {
    if (v == null) return;
    if (depth > maxDepth) {
      rows.push([prefix, toDisplayString(v)]);
      return;
    }

    if (Array.isArray(v)) {
      if (v.length === 0) return;
      const slice = v.slice(0, maxArrayItems);
      slice.forEach((item, i) => {
        walk(`${prefix}[${i}]`, item, depth + 1);
      });
      if (v.length > maxArrayItems) {
        rows.push([`${prefix}[...]`, `+${v.length - maxArrayItems} more`]);
      }
      return;
    }

    if (isPlainObject(v)) {
      const entries = Object.entries(v);
      if (entries.length === 0) return;
      entries.forEach(([k, vv]) => {
        walk(prefix ? `${prefix}.${k}` : k, vv, depth + 1);
      });
      return;
    }

    rows.push([prefix, toDisplayString(v)]);
  };

  walk(key, value, 0);
};

const buildEventDetailRows = (msg) => {
  const rows = [];

  pushIfPresent(rows, 'agentName', msg?.agentName);
  pushIfPresent(rows, 'pipeId', msg?.pipeId);

  const conversationId = msg?.data?.conversation_id ?? msg?.data?.conversationId ?? msg?.conversation_id;
  if (conversationId != null) pushIfPresent(rows, 'conversation_id', conversationId);

  pushIfPresent(rows, 'requestId', msg?.requestId);
  pushIfPresent(rows, 'agentExecId', msg?.agentExecId);

  if (String(msg?.event || '').toLowerCase() === 'agent_execute_error') {
    pushIfPresent(rows, 'error.message', extractAgentExecuteErrorMessage(msg));
  } else {
    pushIfPresent(rows, 'error', msg?.error ?? msg?.data?.error);
  }

  // Goal result (iteration/complete)
  if (msg?.data?.goal_result) {
    pushStructured(rows, 'goal_result', msg.data.goal_result);
  }

  const firstMessage = msg?.data?.messages?.messages?.[0] || msg?.data?.messages?.[0] || null;
  if (firstMessage) {
    pushIfPresent(rows, 'role', firstMessage?.role);
    pushIfPresent(rows, 'message', firstMessage?.content);
    if (firstMessage?.tool_calls) pushStructured(rows, 'tool_calls', firstMessage.tool_calls);
    if (firstMessage?.function_call) pushStructured(rows, 'function_call', firstMessage.function_call);
    pushIfPresent(rows, 'created', firstMessage?.created);
  }

  // Query if present
  pushIfPresent(rows, 'query', msg?.data?.query);

  if (rows.length < 3) {
    pushStructured(rows, 'data', msg?.data);
  }

  // Hide execStatus anywhere it may appear (top-level or nested).
  return rows.filter(([k]) => String(k) !== 'execStatus' && String(k) !== 'data.execStatus');
};

const extractEventTokenUsage = (msg) => {
  const usage =
    msg?.data?.messages?.usage ||
    msg?.data?.messages?.choices?.[0]?.usage ||
    msg?.data?.usage ||
    null;

  const firstMessage = msg?.data?.messages?.messages?.[0] || msg?.data?.messages?.[0] || null;

  const prompt =
    usage?.prompt_tokens ??
    firstMessage?.prompt_tokens ??
    null;
  const completion =
    usage?.completion_tokens ??
    firstMessage?.completion_tokens ??
    null;
  const total =
    usage?.total_tokens ??
    firstMessage?.total_tokens ??
    null;
  const explicitReasoning =
    usage?.reasoning_tokens ??
    firstMessage?.reasoning_tokens ??
    null;
  const nestedReasoning =
    usage?.completion_tokens_details?.reasoning_tokens ??
    null;
  const reasoning = explicitReasoning ?? nestedReasoning ?? null;

  const completionTokensDetails =
    usage?.completion_tokens_details ??
    null;
  const promptTokensDetails =
    usage?.prompt_tokens_details ??
    null;

  const computedTotal =
    typeof total === 'number'
      ? total
      : (typeof prompt === 'number' && typeof completion === 'number')
        ? prompt + completion
        : null;

  const any =
    [prompt, completion, computedTotal, reasoning].some((n) => typeof n === 'number' && !Number.isNaN(n));

  return any
    ? { prompt, completion, total: computedTotal, reasoning, completionTokensDetails, promptTokensDetails }
    : null;
};

const SpanRow = ({ item, isSelected, isExpanded, hasChildren, onToggle, onSelect, onAgentClick, traceId, agentStatusesByScopeKey, traceAgentConfigList }) => {
  const { node, depth } = item;
  const kind = kindStyle(node.kind);
  const taskAgent = isTaskAgentNode(node, traceAgentConfigList);
  const kindForPill =
    String(node.kind || '').toLowerCase() === 'agent' && taskAgent
      ? { bg: '#f5f4ff', color: '#3F51B5', icon: 'fa-solid fa-list-check' }
      : kind;
  const kindLabel =
    String(node.kind || '').toLowerCase() === 'agent' && taskAgent
      ? 'Task Agent'
      : (String(node.kind || '').toLowerCase() === 'agent' ? 'AI Agent' : node.kind);
  const nodeAgentId = resolveAgentIdFromTreeNode(node);
  const nodeScopeKey = `${traceId || 'execution'}::${node?.id || ''}`;
  const nodeRetry = node?.attributes?.retry;
  const fallbackScopeKey = `${traceId || 'execution'}::${String(nodeAgentId)}::retry:${nodeRetry != null ? nodeRetry : 'na'}`;
  const persistedStatus = agentStatusesByScopeKey?.[nodeScopeKey] || agentStatusesByScopeKey?.[fallbackScopeKey] || null;
  // Always show an agent's status immediately from the span tree (node.status),
  // but prefer websocket-derived status when available.
  const displayStatus = persistedStatus || node.status;
  const showStatus =
    String(node.kind || '').toLowerCase() === 'agent' &&
    normalizeStatus(displayStatus) !== 'unknown';
  const isProject = String(node.kind || '').toLowerCase() === 'project';

  const retryNumber = node?.attributes?.retry;
  const showRetryTag =
    String(node.kind || '').toLowerCase() === 'agent' &&
    node?.attributes?.hasMultipleRetries === true &&
    typeof retryNumber === 'number';

  const rowStyles = isSelected
    ? {
        backgroundColor: '#ecfdf3', 
        border: '1px solid #bbf7d0'
      }
    : {
        backgroundColor: 'transparent',
        border: '1px solid transparent'
      };

  return (
    <div
      onClick={() => {
        if (isProject) return;
        if (hasChildren && !isExpanded) onToggle(node.id);
        onSelect(node);
        if (String(node.kind || '').toLowerCase() === 'agent' && typeof onAgentClick === 'function') {
          onAgentClick(node);
        }
      }}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 130px',
        alignItems: 'center',
        padding: '10px 10px',
        borderRadius: '12px',
        margin: '4px 0',
        cursor: isProject ? 'default' : 'pointer',
        ...rowStyles
      }}
      onMouseEnter={(e) => {
        if (!isSelected && !isProject) e.currentTarget.style.backgroundColor = '#f3f4f6';
      }}
      onMouseLeave={(e) => {
        if (!isSelected && !isProject) e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingLeft: depth * 16 }}>
        <div style={{ width: '18px', display: 'flex', justifyContent: 'center' }}>
          {!isProject && hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggle(node.id);
              }}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, color: COLORS.muted }}
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              <i className={`fa-solid ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} style={{ fontSize: '10px' }} />
            </button>
          ) : null}
        </div>
        {isProject ? (
          <div
            style={{
              width: '30px',
              height: '30px',
              borderRadius: '10px',
              // backgroundColor: kind.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <img
              src={diagramPng}
              alt=""
              style={{ width: '30px', height: '30px', objectFit: 'contain' }}
            />
          </div>
        ) : (
          pill({ text: kindLabel, bg: kindForPill.bg, color: kindForPill.color, icon: kindForPill.icon })
        )}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 500, color: COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {node.name}
          </div>
          {showRetryTag ? (
            <span style={{ fontSize: '11px', color: 'rgb(247 124 53)', fontWeight: 600, whiteSpace: 'nowrap' }}>
              retry {retryNumber}
            </span>
          ) : null}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {showStatus ? <StatusBadge status={displayStatus} /> : null}
      </div>
    </div>
  );
};

const getEventColor = (eventName) => {
  const e = String(eventName || '').toLowerCase();
  if (e === 'agent_execute_start') return '#3B82F6'; 
  if (e === 'agent_execute') return '#6366F1';
  if (e === 'agent_llm_execute') return '#8B5CF6'; 
  if (e === 'agent_verify_goal_usage') return '#F59E0B'; 
  if (e === 'agent_iteration_complete') return '#14B8A6'; 
  if (e === 'agent_execute_complete') return '#22C55E';
  if (e === 'agent_execute_error' || e === 'request_failed' || e === 'request_generate') return '#EF4444'; 
  return COLORS.muted;
};

const formatEventLabel = (eventName) => {
  const e = String(eventName || '').toLowerCase();
  switch (e) {
    case 'agent_execute_start':
      return 'Agent Executing Start';
    case 'agent_execute':
      return 'Executing Agent';
    case 'agent_llm_execute':
      return 'Executing LLM';
    case 'agent_verify_goal_usage':
      return 'Verifying Goal Usage';
    case 'agent_iteration_complete':
      return 'Iteration Complete';
    case 'agent_execute_complete':
      return 'Agent Executed';
    case 'agent_execute_error':
      return 'Agent Execution Error';
    default:
      return eventName || '—';
  }
};

const extractAgentExecuteErrorMessage = (msg) => {
  const raw =
    msg?.data?.message ||
    msg?.data?.error ||
    msg?.error ||
    msg?.message ||
    '';

  const text = typeof raw === 'string' ? raw : JSON.stringify(raw);
  if (!text) return '—';

  const m =
    text.match(/['"]message['"]\s*:\s*['"]([\s\S]*?)['"]\s*[,}]/i) ||
    text.match(/message\s*=\s*['"]([\s\S]*?)['"]/i);

  if (m?.[1]) return m[1].trim();

  try {
    const afterDash = text.includes(' - ') ? text.split(' - ').slice(1).join(' - ') : text;
    const normalized = afterDash
      .replace(/\bNone\b/g, 'null')
      .replace(/\bTrue\b/g, 'true')
      .replace(/\bFalse\b/g, 'false')
      .replace(/'/g, '"');
    const parsed = JSON.parse(normalized);
    const nestedMsg = parsed?.error?.message || parsed?.message;
    if (typeof nestedMsg === 'string' && nestedMsg.trim()) return nestedMsg.trim();
  } catch (_) {
    // ignore
  }

  return text;
};

const resolveAgentIdFromTreeNode = (node) => {
  if (!node) return null;
  const attrs = node?.attributes || {};
  const nodeId = typeof node?.id === 'string' ? node.id : '';
  const parsedFromNodeId =
    nodeId.startsWith('agent:') ? nodeId.split('agent:')[1]?.split('::')[0]?.trim() : null;
  return attrs.agentId || attrs.agent_id || attrs?.agentId?.agentId || node?.agentId || parsedFromNodeId || null;
};

const ExecutionHistoryView = ({
  trace,
  execMessages,
  isRefreshing = false,
  executionDurationMs,
  executionDurationsByAgent = {},
  executionApprovalsByAgent = {},
  selectedAgentId = null,
  onBack,
  onClose,
  onAgentClick,
  onRefresh
}) => {
  const [copiedResult, setCopiedResult] = useState(false);
  const [liveWfStatus, setLiveWfStatus] = useState(null);
  const [liveAgentStatuses, setLiveAgentStatuses] = useState({});
  const [liveExecMessages, setLiveExecMessages] = useState(null);

  const traceExecutionId =
    trace?.executionId ||
    trace?.workflowExecutionId ||
    trace?.id ||
    null;
  const traceId =
    trace?.id ||
    trace?.executionId ||
    trace?.workflowExecutionId ||
    'execution';

  const effectiveExecMessages = liveExecMessages ?? execMessages ?? [];
  const websocketAgentStatuses = useMemo(() => ({ ...(liveAgentStatuses || {}) }), [liveAgentStatuses]);

  const root = useMemo(
    () => buildSpanTree({ trace, execMessages: effectiveExecMessages }),
    [trace, effectiveExecMessages]
  );
  const flat = useMemo(() => flattenSpanTree(root), [root]);
  const metrics = useMemo(() => summarizeTraceMetrics(root, trace), [root, trace]);

  // Execution history should not show the workflow console drawer.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('force-close-console-drawer'));
  }, []);

  useEffect(() => {
    setLiveWfStatus(null);
    setLiveAgentStatuses({});
    setLiveExecMessages(null);
  }, [traceExecutionId]);

  useEffect(() => {
    setLiveExecMessages(null);
  }, [execMessages]);

  useEffect(() => {
    if (!traceExecutionId) return undefined;

    const applyWfStatus = (wfStatus, { silentRefresh = false } = {}) => {
      if (!wfStatus) return;
      setLiveWfStatus(wfStatus);
      if (
        silentRefresh &&
        isTerminalWfStatus(wfStatus) &&
        typeof onRefresh === 'function' &&
        selectedAgentId
      ) {
        onRefresh(selectedAgentId, { silent: true });
      }
    };

    const handleWorkflowStatusMessage = (event) => {
      const message = event?.detail;
      if (!message) return;

      if (!messageMatchesTraceExecution(message, trace)) return;

      const traceProjectId = trace?.projectId ?? trace?.project_id;
      const msgProjectId =
        message?.ninfo?.projectId || message?.projectId || message?.project_id;
      if (traceProjectId && msgProjectId && String(traceProjectId) !== String(msgProjectId)) {
        return;
      }

      const wfStatus = resolveWfStatusFromMessage(message);
      applyWfStatus(wfStatus, { silentRefresh: true });
    };

    const handleAgentMessage = (event) => {
      const message = event?.detail;
      if (!message) return;

      const payload = unwrapWorkflowMessage(message);
      if (!payload) return;

      if (!messageMatchesTraceExecution(message, trace)) return;

      const traceProjectId = trace?.projectId ?? trace?.project_id;
      const msgProjectId =
        message?.ninfo?.projectId || message?.projectId || message?.project_id;
      if (traceProjectId && msgProjectId && String(traceProjectId) !== String(msgProjectId)) {
        return;
      }

      if (payload?.event) {
        setLiveExecMessages((prev) => {
          const base = prev ?? execMessages ?? [];
          const dedupeKey =
            payload?.id ??
            `${payload?.event}-${payload?.createdDate ?? payload?.timestamp ?? ''}`;
          if (
            base.some(
              (m) =>
                (m?.id ?? `${m?.event}-${m?.createdDate ?? m?.timestamp ?? ''}`) === dedupeKey
            )
          ) {
            return base;
          }
          return [...base, payload];
        });
      }

      const derivedStatus = deriveAgentStatusFromExecMessage(payload);
      const agentId = resolveAgentIdFromMessage(message);
      if (!derivedStatus || !agentId) return;

      const retry = payload?.data?.retry ?? payload?.retry ?? null;
      setLiveAgentStatuses((prev) => {
        const next = { ...(prev || {}) };
        const matchingNodes = flat.filter(
          ({ node }) => resolveAgentIdFromTreeNode(node) === agentId
        );
        if (matchingNodes.length) {
          matchingNodes.forEach(({ node }) => {
            const scopeKey = `${traceId}::${node.id}`;
            next[scopeKey] = derivedStatus;
            const fallbackScopeKey = `${traceId}::${String(agentId)}::retry:${retry != null ? retry : 'na'}`;
            next[fallbackScopeKey] = derivedStatus;
          });
        } else {
          const fallbackScopeKey = `${traceId}::${String(agentId)}::retry:${retry != null ? retry : 'na'}`;
          next[fallbackScopeKey] = derivedStatus;
        }
        return next;
      });

      const wfStatusFromEvent = resolveWfStatusFromMessage(message);
      if (wfStatusFromEvent) {
        applyWfStatus(wfStatusFromEvent, {
          silentRefresh: isTerminalWfStatus(wfStatusFromEvent)
        });
      }
    };

    window.addEventListener('workflowStatusMessageReceived', handleWorkflowStatusMessage);
    window.addEventListener('agentMessageReceived', handleAgentMessage);
    return () => {
      window.removeEventListener('workflowStatusMessageReceived', handleWorkflowStatusMessage);
      window.removeEventListener('agentMessageReceived', handleAgentMessage);
    };
  }, [traceExecutionId, trace, traceId, flat, execMessages, onRefresh, selectedAgentId]);

  const firstAgentNode = useMemo(() => {
    const firstAgent = (flat || []).find(
      ({ node }) =>
        node &&
        node.id !== root.id &&
        String(node.kind || '').toLowerCase() === 'agent'
    );
    return firstAgent?.node || null;
  }, [flat, root]);

  const [expanded, setExpanded] = useState(() => new Set([root.id, ...(root.children || []).map((c) => c.id)]));
  const [selectedId, setSelectedId] = useState(() => (firstAgentNode?.id || root.children?.[0]?.id || root.id));
  const [tab, setTab] = useState('attributes');
  const [expandedEvents, setExpandedEvents] = useState(() => new Set());
  const [expandedTokenRows, setExpandedTokenRows] = useState(() => new Set());
  const initialFetchKeyRef = useRef(null);

  const selectedNode = useMemo(() => {
    const found = flat.find((x) => x.node.id === selectedId);
    return found?.node || root;
  }, [flat, selectedId, root]);

  useEffect(() => {
    // If selected id disappears, reset to first Agent node (or first child, or root).
    if (!flat.some((x) => x.node.id === selectedId)) {
      setSelectedId(firstAgentNode?.id || root.children?.[0]?.id || root.id);
    }
  }, [flat, selectedId, root, firstAgentNode]);

  useEffect(() => {
    if (!firstAgentNode || typeof onAgentClick !== 'function') return;
    const traceId =
      trace?.id ||
      trace?.executionId ||
      trace?.workflowExecutionId ||
      'trace';
    const firstAgentId = resolveAgentIdFromTreeNode(firstAgentNode);
    if (!firstAgentId) return;
    const fetchKey = `${traceId}::${String(firstAgentId)}`;
    // Ensure first agent gets loaded only once for this trace/agent.
    if (initialFetchKeyRef.current === fetchKey) return;
    const firstNodeScopeKey = `${traceId}::${firstAgentNode?.id || ''}`;
    const firstFallbackScopeKey = `${traceId}::${String(firstAgentId)}::retry:${firstAgentNode?.attributes?.retry != null ? firstAgentNode.attributes.retry : 'na'}`;
    if (websocketAgentStatuses?.[firstNodeScopeKey] || websocketAgentStatuses?.[firstFallbackScopeKey]) {
      initialFetchKeyRef.current = fetchKey;
      return;
    }
    initialFetchKeyRef.current = fetchKey;
    onAgentClick(firstAgentNode);
  }, [trace, traceId, firstAgentNode, onAgentClick, websocketAgentStatuses]);

  const traceAgentConfigList = useMemo(() => {
    const list = trace?.appConfig?.agentConfig ?? trace?.agentConfig ?? [];
    return Array.isArray(list) ? list : [];
  }, [trace]);

  const visible = useMemo(() => {
    const byId = new Map(flat.map((x) => [x.node.id, x]));
    const isVisible = (id) => {
      let cur = byId.get(id);
      while (cur?.parent) {
        if (!expanded.has(cur.parent.id)) return false;
        cur = byId.get(cur.parent.id);
      }
      return true;
    };
    return flat.filter((x) => isVisible(x.node.id));
  }, [flat, expanded]);

  const traceStatus = normalizeStatus(root.status || trace?.status);

  const headerTitle = trace?.name || root.name || 'Execution';
  const traceDurationMs =
    typeof trace?.duration?.durationInMillis === 'number'
      ? trace.duration.durationInMillis
      : (typeof trace?.durationMs === 'number'
          ? trace.durationMs
          : (typeof trace?.duration === 'number' ? trace.duration : null));
  const headerDuration = typeof traceDurationMs === 'number' ? formatDuration(traceDurationMs) : '—';
  const headerSpans = metrics.spansCount?.toLocaleString?.() ?? String(metrics.spansCount ?? '—');
  const executedBy = trace?.updatedBy || '—';  

  const selectedKindKey = String(selectedNode?.kind || '').toLowerCase();
  const selectedIsAgent = selectedKindKey === 'agent';
  const selectedIsTaskAgent = isTaskAgentNode(selectedNode, traceAgentConfigList);
  const selectedDetailKindLabel = selectedIsAgent
    ? (selectedIsTaskAgent ? 'Task Agent' : 'AI Agent')
    : selectedNode.kind;
  const selectedDetailKindStyle = selectedIsAgent
    ? (selectedIsTaskAgent
        ? { bg: '#f5f4ff', color: '#3F51B5', icon: 'fa-solid fa-list-check' }
        : kindStyle('agent'))
    : kindStyle(selectedNode.kind);
  const selectedStatus = normalizeStatus(selectedNode.status);

  const matchedAgentConfig = useMemo(() => {
    const isAgentNode = String(selectedNode?.kind || '').toLowerCase() === 'agent';
    if (!isAgentNode) return null;

    const selectedAgentId =
      selectedNode?.attributes?.agentId ||
      selectedNode?.attributes?.agent_id ||
      selectedNode?.attributes?.agentId?.agentId;

    const selectedName =
      selectedNode?.attributes?.agentName ||
      selectedNode?.name ||
      selectedNode?.attributes?.name ||
      null;

    return (
      traceAgentConfigList.find((cfg) => cfg?.agentId && selectedAgentId && cfg.agentId === selectedAgentId) ||
      traceAgentConfigList.find((cfg) => {
        if (!selectedName) return false;
        return cfg?.title === selectedName || cfg?.name === selectedName;
      }) ||
      null
    );
  }, [traceAgentConfigList, selectedNode]);

  const roleFromTrace = matchedAgentConfig?.role ?? null;
  const goalFromTrace = matchedAgentConfig?.goal ?? null;
  const promptTemplateFromTrace = matchedAgentConfig?.promptTemplate ?? null;

  const promptTemplateCell = useMemo(() => {
    if (selectedIsTaskAgent) return null;
    if (promptTemplateFromTrace == null) return null;

    if (typeof promptTemplateFromTrace === 'string') {
      const s = promptTemplateFromTrace.trim();
      if (!s) return null;
      return (
        <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.35, wordBreak: 'break-word' }}>
          {promptTemplateFromTrace}
        </div>
      );
    }

    // If promptTemplate is not a string, render a stable JSON representation.
    try {
      return (
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.35, wordBreak: 'break-word' }}>
          {JSON.stringify(promptTemplateFromTrace, null, 2)}
        </pre>
      );
    } catch (_) {
      return String(promptTemplateFromTrace);
    }
  }, [promptTemplateFromTrace, selectedIsTaskAgent]);

  const isModelOrToolAttributeKey = (key) => {
    const k = String(key || '').toLowerCase();
    return k === 'model' || k === 'llm' || k === 'llm.model' || k.includes('tool');
  };

  const attrRows = useMemo(() => {
    const base = Object.entries(selectedNode.attributes || {}).filter(([key]) => {
      if (!selectedIsTaskAgent) return true;
      return !isModelOrToolAttributeKey(key);
    });
    if (!matchedAgentConfig || selectedIsTaskAgent) return base;

    const rows = base.slice();
    if (roleFromTrace != null) rows.push(['Role', roleFromTrace]);
    if (goalFromTrace != null) rows.push(['Goal', goalFromTrace]);
    return rows;
  }, [selectedNode, matchedAgentConfig, roleFromTrace, goalFromTrace, selectedIsTaskAgent]);

  const execMsgs = useMemo(() => {
    if (!effectiveExecMessages) return [];
    if (Array.isArray(effectiveExecMessages)) return effectiveExecMessages;
    if (Array.isArray(effectiveExecMessages.execMsgDTOS)) return effectiveExecMessages.execMsgDTOS;
    return [];
  }, [effectiveExecMessages]);

  // Header status should reflect workflow wfStatus only.
  // It must not change due to agent clicks or agent websocket events.
  const headerStatus = useMemo(
    () => trace?.wfStatus ?? trace?.wf_status ?? null,
    [trace]
  );

  const execMsgsOrdered = useMemo(() => preprocessExecMessages(execMsgs), [execMsgs]);
  const messageRetryMap = useMemo(() => buildMessageRetryMap(execMsgsOrdered), [execMsgsOrdered]);

  // Resolve active agent/retry scope from the selected node OR its ancestors.
  const selectedExecutionScope = useMemo(() => {
    if (!selectedNode || selectedNode.id === root?.id) return null;
    const byId = new Map(flat.map((x) => [x.node.id, x]));
    let cur = byId.get(selectedNode.id);

    while (cur?.node) {
      const node = cur.node;
      const agentIdAttr = resolveAgentIdFromTreeNode(node);
      if (agentIdAttr) {
        return {
          agentId: agentIdAttr,
          retry: node?.attributes?.retry ?? null
        };
      }
      cur = cur.parent ? byId.get(cur.parent.id) : null;
    }
    return null;
  }, [flat, selectedNode, root]);

  const handleHeaderRefresh = () => {
    if (typeof onRefresh !== 'function') return;
    const refreshAgentId =
      selectedExecutionScope?.agentId ||
      resolveAgentIdFromTreeNode(selectedNode) ||
      null;
    onRefresh(refreshAgentId);
  };

  const selectedScopeDurationMs = useMemo(() => {
    const selectedAgentId = selectedExecutionScope?.agentId;
    if (selectedAgentId && typeof executionDurationsByAgent?.[selectedAgentId] === 'number') {
      return executionDurationsByAgent[selectedAgentId];
    }
    if (typeof selectedNode?.durationMs === 'number') return selectedNode.durationMs;
    if (typeof executionDurationMs === 'number') return executionDurationMs;
    return typeof metrics.durationMs === 'number' ? metrics.durationMs : null;
  }, [selectedExecutionScope, executionDurationsByAgent, selectedNode, executionDurationMs, metrics.durationMs]);

  /** Events/tokens/query for selected scope: full trace at root; per-agent + retry for retry trees. */
  const execMsgsInWindow = useMemo(() => {
    if (!selectedExecutionScope?.agentId) return execMsgsOrdered;
    return execMsgsOrdered.filter((m) => {
      if (String(m?.agentId) !== String(selectedExecutionScope.agentId)) return false;
      if (selectedExecutionScope.retry == null) return true;
      return (messageRetryMap.get(m) ?? 1) === selectedExecutionScope.retry;
    });
  }, [execMsgsOrdered, messageRetryMap, selectedExecutionScope]);

  const eventRows = execMsgsInWindow.map((msg, idx) => {
    const eventName = msg.event || `event.${idx + 1}`;
    const status = msg.execStatus || msg.status || '—';
    const color = getEventColor(eventName);
    const e = String(eventName || '').toLowerCase();

    const value =
      e === 'agent_execute_error'
        ? extractAgentExecuteErrorMessage(msg)
        : String(status).toUpperCase();

    return [
      <span key={`event-${idx}`} style={{ color }}>{formatEventLabel(eventName)}</span>,
      <span key={`status-${idx}`} style={{ color }}>{value}</span>
    ];
  });

  const eventAccordionItems = execMsgsInWindow.map((msg, idx) => {
    const eventName = msg?.event || `event.${idx + 1}`;
    const color = getEventColor(eventName);
    const id = `evt:${idx}:${msg?.requestId || ''}:${msg?.agentExecId || ''}:${normalizeEventKey(eventName)}`;
    const isOpen = expandedEvents.has(id);

    const conversationId =
      msg?.data?.conversation_id ?? msg?.data?.conversationId ?? msg?.conversation_id;
    const headerMeta = [
      msg?.agentName ? `Agent: ${msg.agentName}` : null,
      msg?.pipeId ? `Pipe: ${msg.pipeId}` : null,
      conversationId != null ? `conversation_id: ${conversationId}` : null
    ].filter(Boolean);

    return (
      <div
        key={id}
        style={{
          border: `1px solid ${COLORS.border}`,
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: COLORS.bg,
          marginBottom: '10px'
        }}
      >
        <button
          type="button"
          onClick={() => {
            setExpandedEvents((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            });
          }}
          style={{
            width: '100%',
            textAlign: 'left',
            border: 'none',
            background: '#f8fafc',
            padding: '12px 14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
              <i className={`fa-solid ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`} style={{ fontSize: '11px', color: COLORS.muted }} />
              <span style={{ color, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>
                {formatEventLabel(eventName)}
              </span>
            </div>
            {headerMeta.length > 0 ? (
              <div
                style={{
                  marginTop: '6px',
                  color: COLORS.muted,
                  fontSize: '12px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {headerMeta.join(' · ')}
              </div>
            ) : null}
          </div>
          <span style={{ color: COLORS.muted, fontSize: '12px', fontWeight: 600 }}>{`#${idx + 1}`}</span>
        </button>

        {isOpen ? (
          <div style={{ padding: '12px 12px 14px' }}>
                      <Table rows={buildEventDetailRows(msg)} headerKey="Key" headerValue="Value" />
          </div>
        ) : null}
      </div>
    );
  });

  const eventsFallbackError = useMemo(() => {
    // If scoped events are empty (common when backend returns agentId=null),
    // fall back to the full execution message list.
    const list =
      Array.isArray(execMsgsInWindow) && execMsgsInWindow.length
        ? execMsgsInWindow
        : (Array.isArray(execMsgsOrdered) ? execMsgsOrdered : []);
    if (!list.length) return null;
    for (let i = list.length - 1; i >= 0; i -= 1) {
      const m = list[i];
      const err =
        m?.error ||
        m?.data?.error ||
        m?.data?.errorMessage ||
        m?.message ||
        null;
      if (typeof err === 'string' && err.trim()) {
        const ev = m?.event ? String(m.event) : null;
        return ev ? `${ev}: ${err.trim()}` : err.trim();
      }
    }
    return null;
  }, [execMsgsInWindow, execMsgsOrdered]);

  const eventsTabCount = useMemo(() => {
    if (eventAccordionItems.length > 0) return eventAccordionItems.length;
    return eventsFallbackError ? 1 : 0;
  }, [eventAccordionItems.length, eventsFallbackError]);

  const tokenMessagesInScope = useMemo(() => {
    if (!selectedExecutionScope?.agentId) return [];
    return execMsgsInWindow.filter((m) => String(m?.agentId) === String(selectedExecutionScope.agentId));
  }, [execMsgsInWindow, selectedExecutionScope]);

  const tokenItems = useMemo(() => {
    if (!tokenMessagesInScope.length) return [];
    return tokenMessagesInScope
      .map((msg, idx) => {
        const eventName = msg?.event || `event.${idx + 1}`;
        const u = extractEventTokenUsage(msg);
        if (!u) return null;
        const id = `${msg?.requestId || ''}:${msg?.agentExecId || ''}:${idx}:${eventName}:tokens`;
        return {
          id,
          event: formatEventLabel(eventName),
          prompt: typeof u.prompt === 'number' ? u.prompt.toLocaleString() : u.prompt,
          completion: typeof u.completion === 'number' ? u.completion.toLocaleString() : u.completion,
          reasoning: typeof u.reasoning === 'number' ? u.reasoning.toLocaleString() : u.reasoning,
          total: typeof u.total === 'number' ? u.total.toLocaleString() : u.total,
          completionTokensDetails: u.completionTokensDetails,
          promptTokensDetails: u.promptTokensDetails
        };
      })
      .filter(Boolean);
  }, [tokenMessagesInScope]);

  // Total tokens computed from the same data used in the Tokens tab.
  const scopedTotalTokens = useMemo(() => {
    if (!tokenMessagesInScope.length) return null;
    let sum = 0;
    let hasAny = false;
    tokenMessagesInScope.forEach((msg) => {
      const u = extractEventTokenUsage(msg);
      if (typeof u?.total === 'number' && !Number.isNaN(u.total)) {
        sum += u.total;
        hasAny = true;
      }
    });
    return hasAny ? sum : null;
  }, [tokenMessagesInScope]);

  const totalTokens = useMemo(() => {
    const raw =
      trace?.totalTokens ??
      trace?.tokens ??
      trace?.tokenCount ??
      trace?.token_count ??
      null;
    const n =
      typeof raw === 'number'
        ? raw
        : (typeof raw === 'string' && raw.trim() !== '' && !Number.isNaN(Number(raw)) ? Number(raw) : null);
    return typeof n === 'number' ? n.toLocaleString() : '—';
  }, [trace]);

  const metricTotalTokens = useMemo(() => {
    if (typeof scopedTotalTokens === 'number') return scopedTotalTokens.toLocaleString();
    return '0';
  }, [scopedTotalTokens]);

  const firstWithQuery = execMsgsInWindow.find((m) => m?.data && (m.data.query || m.data.goal_result));
  const goalResultSource =
    execMsgsInWindow.find((m) => m?.data?.goal_result) || null;
  const goalResult = goalResultSource?.data?.goal_result || null;

  const queryText = firstWithQuery?.data?.query ?? '';
  const resultText = goalResult?.result ?? '';
  const reasonText = goalResult?.reason ?? '';

  const handleCopyResult = async () => {
    try {
      const text = (() => {
        if (resultText == null) return '';
        if (typeof resultText === 'string') return resultText;
        try {
          return JSON.stringify(resultText, null, 2);
        } catch (_) {
          return String(resultText);
        }
      })();
      if (!text) return;
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        el.setAttribute('readonly', '');
        el.style.position = 'fixed';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setCopiedResult(true);
      window.setTimeout(() => setCopiedResult(false), 1200);
    } catch (_) {
      // ignore
    }
  };

  const queryAndResultRows = [];
  if (firstWithQuery?.data?.query) {
    queryAndResultRows.push(['Query', firstWithQuery.data.query]);
  }
  if (goalResult?.result) {
    queryAndResultRows.push(['Result', goalResult.result]);
  }
  if (goalResult?.reason != null && String(goalResult.reason).trim() !== '') {
    queryAndResultRows.push(['Reason', goalResult.reason]);
  }

  const approvalRecords = useMemo(() => {
    const seen = new Set();
    const list = [];
    const add = (raw) => {
      if (!hasApprovalPayload(raw)) return;
      const approval = normalizeApproval(raw);
      const key = approval.requestId
        || `${approval.agentId}:${approval.runId}:${approval.respondedAt}:${approval.approvalStatus}`;
      if (seen.has(key)) return;
      seen.add(key);
      list.push(approval);
    };

    const scopedAgentId = selectedExecutionScope?.agentId || selectedAgentId;
    const isRoot = !selectedNode || selectedNode.id === root?.id;
    const approvalsMap = executionApprovalsByAgent || {};

    if (isRoot) {
      Object.values(approvalsMap).forEach(add);
    } else {
      if (scopedAgentId) add(approvalsMap[scopedAgentId]);
      if (selectedAgentId && String(selectedAgentId) !== String(scopedAgentId)) {
        add(approvalsMap[selectedAgentId]);
      }
      Object.entries(approvalsMap).forEach(([agentId, raw]) => {
        if (scopedAgentId && String(agentId) === String(scopedAgentId)) return;
        if (!hasApprovalPayload(raw)) return;
        const rawAgentId = raw.agentId || raw.agent_id;
        if (scopedAgentId && String(rawAgentId) === String(scopedAgentId)) add(raw);
      });
    }

    for (const msg of execMsgsInWindow) add(pickApprovalFromMessage(msg));
    return list;
  }, [
    execMsgsInWindow,
    executionApprovalsByAgent,
    selectedExecutionScope,
    selectedAgentId,
    selectedNode,
    root
  ]);

  useEffect(() => {
    if (tab === 'approval' && approvalRecords.length === 0) {
      setTab('attributes');
    }
  }, [tab, approvalRecords.length]);

  const model = useMemo(() => {
    if (isNodeUnderTaskAgent(selectedNode, flat, traceAgentConfigList)) return '—';

    const fromSelectedNode =
      selectedNode?.attributes?.['llm.model'] ||
      selectedNode?.attributes?.model ||
      null;
    if (fromSelectedNode) return String(fromSelectedNode);

    const llmMessage = execMsgsInWindow.find((m) => {
      const e = String(m?.event || '').toLowerCase();
      return e === 'agent_llm_execute' || e === 'agent_verify_goal_usage';
    });
    const fromMessages =
      llmMessage?.data?.messages?.model ||
      llmMessage?.data?.model ||
      llmMessage?.data?.llm?.model ||
      null;
    if (fromMessages) return String(fromMessages);

    const llmType = matchedAgentConfig?.llm || matchedAgentConfig?.llmType || null;
    if (llmType) {
      const llmConfig = (trace?.appConfig?.llm || []).find((l) => l?.llmType === llmType);
      const fromConfig = llmConfig?.llmConfig?.model || llmConfig?.title || null;
      if (fromConfig) return String(fromConfig);
    }

    return trace?.model ? String(trace.model) : '—';
  }, [selectedNode, flat, traceAgentConfigList, execMsgsInWindow, matchedAgentConfig, trace]);
  return (
    <div style={{ position: 'fixed', inset: 0, width: '100%', height: '100vh', backgroundColor: COLORS.bg, zIndex: 2000, display: 'flex', flexDirection: 'column' }}>
      {/* Top Header */}
      <div style={{ padding: '18px 22px 14px', borderBottom: `1px solid ${COLORS.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
            <button
              onClick={onBack}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '6px', color: COLORS.muted }}
              title="Back"
            >
              <i className="fa-solid fa-arrow-left" />
            </button>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                <div style={{ fontSize: '22px', fontWeight: 600, color: COLORS.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {headerTitle}
                </div>
                {headerStatus ? <StatusBadge status={headerStatus} /> : null}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', alignSelf: 'flex-start' }}>
              {typeof onRefresh === 'function' ? (
                <button
                  type="button"
                  onClick={handleHeaderRefresh}
                  disabled={isRefreshing}
                  style={{
                    border: `1px solid ${COLORS.border}`,
                    backgroundColor: COLORS.bg,
                    padding: '8px 10px',
                    borderRadius: '10px',
                    cursor: isRefreshing ? 'not-allowed' : 'pointer',
                    opacity: isRefreshing ? 0.65 : 1,
                    color: COLORS.text,
                    fontWeight: 600,
                    fontSize: '13px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  title="Refresh"
                >
                  <i className={`fa-solid ${isRefreshing ? 'fa-spinner fa-spin' : 'fa-rotate'}`} />
                  Refresh
                </button>
              ) : null}
              {typeof onBack === 'function' ? (
                <button
                  onClick={onBack}
                  style={{
                    border: `1px solid ${COLORS.border}`,
                    backgroundColor: COLORS.bg,
                    padding: '8px 10px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    color: COLORS.text,
                    fontWeight: 600,
                    fontSize: '13px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  title="Close"
                >
                  <i className="fa-solid fa-xmark" />
                  Close
                </button>
              ) : null}
            </div>
          </div>

        <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '22px', color: COLORS.muted, fontSize: '13px', fontWeight: 500 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <i className="fa-regular fa-clock" />
            {headerDuration}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <i className="fa-solid fa-diagram-project" />
            {headerSpans} spans
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <i className="fa-solid fa-bolt" />
            {totalTokens} tokens
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <i className="fa-solid fa-user" />
            {executedBy}
          </span>
        </div>
      </div>

      {/* Main Split */}
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '42% 58%', minHeight: 0 }}>
        {/* Left: Span Tree */}
        <div style={{ borderRight: `1px solid ${COLORS.border}`, backgroundColor: COLORS.bg, minHeight: 0, overflow: 'auto' }}>
          <div style={{ padding: '14px 22px', color: COLORS.muted, fontSize: '13px', fontWeight: 600, letterSpacing: '0.06em' }}>SPAN TREE</div>
          <div style={{ padding: '0 14px 16px' }}>
            {visible.map((item) => {
              const node = item.node;
              const hasChildren = Array.isArray(node.children) && node.children.length > 0;
              const isExpanded = expanded.has(node.id);
              return (
                <SpanRow
                  key={node.id}
                  item={item}
                  isSelected={node.id === selectedId}
                  isExpanded={isExpanded}
                  hasChildren={hasChildren}
                  traceId={traceId}
                  traceAgentConfigList={traceAgentConfigList}
                  agentStatusesByScopeKey={websocketAgentStatuses}
                  onToggle={(id) => {
                    setExpanded((prev) => {
                      const next = new Set(prev);
                      if (next.has(id)) next.delete(id);
                      else next.add(id);
                      return next;
                    });
                  }}
                  onSelect={(n) => setSelectedId(n.id)}
                  onAgentClick={onAgentClick}
                />
              );
            })}
          </div>
        </div>

        {/* Right: Details */}
        <div style={{ backgroundColor: COLORS.bg, minHeight: 0, overflow: 'auto' }}>
          <div style={{ padding: '18px 22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {pill({ text: selectedDetailKindLabel, bg: selectedDetailKindStyle.bg, color: selectedDetailKindStyle.color, icon: selectedDetailKindStyle.icon })}
              {/* {String(selectedNode.kind || '').toLowerCase() === 'agent' ? <StatusBadge status={selectedStatus} /> : null} */}
            </div>

            <div style={{ marginTop: '10px', fontSize: '26px', fontWeight: 600, color: COLORS.text }}>{selectedNode.name}</div>
          
            <div style={{ marginTop: '18px', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px' }}>
              <MetricCard
                icon="fa-regular fa-clock"
                label="Duration"
                value={typeof selectedScopeDurationMs === 'number' ? formatDuration(selectedScopeDurationMs) : '—'}
                iconBackgroundColor="#a78bfa"
                cardBackgroundColor="#f5f3ff"
              />
              <MetricCard
                icon="fa-solid fa-robot"
                label="Model"
                value={String(model)}
                iconBackgroundColor="#38bdf8"
                cardBackgroundColor="#f0f9ff"
              />
              <MetricCard
                icon="fa-solid fa-bolt"
                label="Total Tokens"
                value={metricTotalTokens}
                iconBackgroundColor="#34d399"
                cardBackgroundColor="#ecfdf5"
              />
            </div>

            <div style={{ marginTop: '18px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setTab('attributes')}
                style={{
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: tab === 'attributes' ? '#f3f4f6' : COLORS.bg,
                  padding: '10px 14px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: COLORS.text
                }}
              >
                Attributes ({attrRows.length})
              </button>
              <button
                onClick={() => setTab('events')}
                style={{
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: tab === 'events' ? '#f3f4f6' : COLORS.bg,
                  padding: '10px 14px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: COLORS.text
                }}
              >
                Events ({eventsTabCount})
              </button>
              <button
                onClick={() => setTab('tokens')}
                style={{
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: tab === 'tokens' ? '#f3f4f6' : COLORS.bg,
                  padding: '10px 14px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: COLORS.text
                }}
              >
                Tokens ({tokenItems.length})
              </button>
              <button
                onClick={() => setTab('query')}
                style={{
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: tab === 'query' ? '#f3f4f6' : COLORS.bg,
                  padding: '10px 14px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: COLORS.text
                }}
              >
                Query &amp; Result ({queryAndResultRows.length})
              </button>
              {approvalRecords.length > 0 ? (
                <button
                  onClick={() => setTab('approval')}
                  style={{
                    border: `1px solid ${COLORS.border}`,
                    backgroundColor: tab === 'approval' ? '#f3f4f6' : COLORS.bg,
                    padding: '10px 14px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: COLORS.text
                  }}
                >
                  Approval ({approvalRecords.length})
                </button>
              ) : null}
            </div>

            <div style={{ marginTop: '12px' }}>
              {tab === 'attributes' && (
                <div>
                  <Table
                    style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr 1fr', borderBottom: `1px solid ${COLORS.border}`, background: 'linear-gradient(90deg, #3b82f614 0%, #8b5cf614 50%, #f8fafc 100%)' }}
                    rows={attrRows}
                  />
                  {promptTemplateCell ? (
                    <div style={{ marginTop: '12px' }}>
                      <Table rows={[['Prompt', promptTemplateCell]]} headerKey="Key" headerValue="Value" accentColor="#a78bfa" />
                    </div>
                  ) : null}
                </div>
              )}
              {tab === 'events' && (
                <div>
                  {eventAccordionItems.length === 0 ? (
                    eventsFallbackError ? (
                      <Table rows={[['Error', eventsFallbackError]]} headerKey="Key" headerValue="Value" accentColor={COLORS.red} />
                    ) : (
                      <Table rows={[]} headerKey="Event" headerValue="Details" style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr 1fr', borderBottom: `1px solid ${COLORS.border}`, background: 'linear-gradient(90deg, #3b82f614 0%, #8b5cf614 50%, #f8fafc 100%)' }}/>
                    )
                  ) : (
                    eventAccordionItems
                  )}
                </div>
              )}
              {tab === 'tokens' && (
                <TokenAccordion
                  expandedIds={expandedTokenRows}
                  onToggle={(id) => {
                    setExpandedTokenRows((prev) => {
                      const next = new Set(prev);
                      if (next.has(id)) next.delete(id);
                      else next.add(id);
                      return next;
                    });
                  }}
                  items={tokenItems}
                />
              )}
              {tab === 'query' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                  <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 12, background: COLORS.bg, overflow: 'hidden' }}>
                    <div style={{ padding: '12px 14px', borderBottom: `1px solid ${COLORS.border}`, background: 'linear-gradient(90deg, #3b82f614 0%, #8b5cf614 50%, #f8fafc 100%)', fontSize: 12, fontWeight: 800, color: '#7c3aed', letterSpacing: '0.06em' }}>
                      QUERY
                    </div>
                    <div style={{ padding: '14px' }}>
                      <MarkdownBlock text={queryText} />
                    </div>
                  </div>

                  <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 12, background: COLORS.bg, overflow: 'hidden' }}>
                    <div style={{ padding: '12px 14px', borderBottom: `1px solid ${COLORS.border}`, background: 'linear-gradient(90deg, #10b98114 0%, #3b82f610 55%, #f8fafc 100%)', fontSize: 12, fontWeight: 800, color: '#0f766e', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <span>RESULT</span>
                      <button
                        type="button"
                        onClick={handleCopyResult}
                        disabled={!String(resultText ?? '').trim()}
                        style={{
                          border: `1px solid ${COLORS.border}`,
                          backgroundColor: COLORS.bg,
                          padding: '6px 10px',
                          borderRadius: '10px',
                          cursor: String(resultText ?? '').trim() ? 'pointer' : 'not-allowed',
                          opacity: String(resultText ?? '').trim() ? 1 : 0.55,
                          color: COLORS.text,
                          fontWeight: 700,
                          fontSize: '12px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          letterSpacing: 'normal'
                        }}
                        title="Copy Result"
                      >
                        <i className={`fa-solid ${copiedResult ? 'fa-check' : 'fa-copy'}`} />
                        {copiedResult ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <div style={{ padding: '14px' }}>
                      <MarkdownBlock text={resultText} />
                    </div>
                  </div>

                  {String(reasonText ?? '').trim() !== '' ? (
                    <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 12, background: COLORS.bg, overflow: 'hidden' }}>
                      <div style={{ padding: '12px 14px', borderBottom: `1px solid ${COLORS.border}`, background: 'linear-gradient(90deg, #f59e0b14 0%, #f8fafc 65%)', fontSize: 12, fontWeight: 800, color: '#b45309', letterSpacing: '0.06em' }}>
                        REASON
                      </div>
                      <div style={{ padding: '14px' }}>
                        <MarkdownBlock text={reasonText} />
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
              {tab === 'approval' && approvalRecords.length > 0 && (
                <div style={{ display: 'grid', gap: 16 }}>
                  {approvalRecords.map((approval, idx) => (
                    <ApprovalCard
                      key={approval.requestId || `${approval.agentId || 'approval'}-${idx}`}
                      approval={approval}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutionHistoryView;


